// src/pages/doctors/DoctorList.js
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../../utils/config";
import Modal from "../../components/Modal";

export default function DoctorList() {
  const token = localStorage.getItem("token");

  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // search
  const [q, setQ] = useState("");

  // payment modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [form, setForm] = useState({
    amount: "",
    remark: "",
    payment_mode: "Cash",
    payment_date: new Date().toISOString().slice(0, 10),
  });

  const fmtINR = (n) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(Number(n || 0));

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get(`${API_BASE_URL}/api/doctors`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const doctorsWithLinked = (res.data || []).map((d) => ({
        ...d,
        linked_customer:
          d.customers && d.customers.length > 0
            ? {
                customer_id: d.customers[0].customer_id,
                name: d.customers[0].customer_name,
                type: d.customers[0].type,
                commission_percent: d.customers[0].commission_value,
                commission_type: d.customers[0].commission_type,
              }
            : null,
      }));
      setDoctors(doctorsWithLinked);
    } catch (e) {
      console.error("Error fetching doctors:", e);
      setError("Failed to load doctors.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddPaymentClick = (doctor) => {
    setSelectedDoctor(doctor);
    setForm((f) => ({
      ...f,
      // prefill with balance if any
      amount:
        Number(doctor?.balance_commission || 0) > 0
          ? Number(doctor.balance_commission).toFixed(2)
          : "",
      remark: "",
      payment_mode: "Cash",
      payment_date: new Date().toISOString().slice(0, 10),
    }));
    setIsModalOpen(true);
  };

  const handlePaymentSubmit = async () => {
    if (!selectedDoctor) return;
    if (!form.amount || Number(form.amount) <= 0) {
      alert("Enter a valid amount.");
      return;
    }
    try {
      await axios.post(
        `${API_BASE_URL}/api/doctor-commission-payments`,
        {
          doctor_id: selectedDoctor.id,
          ...form,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsModalOpen(false);
      await load();
    } catch (error) {
      console.error("Error saving payment:", error);
      alert("Error saving payment");
    }
  };

  const typeBadge = (t) => {
    const map = {
      all_products: { label: "All products", tone: "bg-emerald-50 text-emerald-700" },
      product_specific: { label: "Product specific", tone: "bg-blue-50 text-blue-700" },
      excluded_products: { label: "Excluded products", tone: "bg-rose-50 text-rose-700" },
    };
    const it = map[t] || { label: t || "-", tone: "bg-slate-100 text-slate-700" };
    return (
      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${it.tone}`}>
        {it.label}
      </span>
    );
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return doctors;
    return doctors.filter((d) => {
      const inName = (d.name || "").toLowerCase().includes(term);
      const inCust = (d.linked_customer?.name || "").toLowerCase().includes(term);
      return inName || inCust;
    });
  }, [doctors, q]);

  return (
    <div className="p-4 md:p-6">
      {/* Header / actions */}
      <div className="mx-auto mb-4 flex max-w-6xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Doctor List</h2>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or customer…"
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:w-72"
          />
          <Link
            to="/doctors/add"
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 active:scale-[.98]"
          >
            + Add Doctor
          </Link>
        </div>
      </div>

      {/* Error / Loading */}
      {error && (
        <div className="mx-auto mb-3 max-w-6xl rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}
      {loading && (
        <div className="mx-auto max-w-6xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          Loading…
        </div>
      )}

      {!loading && (
        <div className="mx-auto max-w-6xl rounded-2xl border border-slate-200 bg-white shadow-sm">
          {/* Desktop table */}
          <div className="hidden overflow-x-auto sm:block">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-700">
                  <th className="px-3 py-2 text-left font-medium">Doctor Name</th>
                  <th className="px-3 py-2 text-left font-medium">Linked Customer</th>
                  <th className="px-3 py-2 text-right font-medium">Total Commission</th>
                  <th className="px-3 py-2 text-right font-medium">Commission Paid</th>
                  <th className="px-3 py-2 text-right font-medium">Balance</th>
                  <th className="px-3 py-2 text-left font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="[&>tr:nth-child(even)]:bg-slate-50">
                {filtered.map((doctor) => (
                  <tr key={doctor.id} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <Link
                        to={`/doctors/${doctor.id}`}
                        className="font-medium text-blue-700 hover:underline"
                      >
                        {doctor.name}
                      </Link>
                    </td>

                    <td className="px-3 py-2">
  {doctor.customers && doctor.customers.length ? (
    <div className="flex flex-wrap gap-1">
      {doctor.customers.map((c) => (
        <Link
          key={c.customer_id}
          to={`/customers/${c.customer_id}`}
          className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-200"
        >
          {c.customer_name}
        </Link>
      ))}
    </div>
  ) : (
    <span className="text-slate-400">—</span>
  )}
</td>


                    <td className="px-3 py-2 text-right tabular-nums">
                      {fmtINR(doctor.total_commission)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {fmtINR(doctor.commission_paid)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums ${
                        Number(doctor.balance_commission || 0) > 0
                          ? "text-rose-600 font-semibold"
                          : "text-slate-700"
                      }`}
                    >
                      {fmtINR(doctor.balance_commission)}
                    </td>

                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/doctors/edit/${doctor.id}`}
                          className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => handleAddPaymentClick(doctor)}
                          className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-emerald-700"
                        >
                          Add Payment
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                      No matching doctors found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="divide-y divide-slate-100 sm:hidden">
            {filtered.length === 0 && (
              <div className="p-4 text-center text-sm text-slate-500">No doctors found.</div>
            )}
            {filtered.map((d) => (
              <div key={d.id} className="p-4">
                <div className="flex items-start justify-between">
                  <Link
                    to={`/doctors/${d.id}`}
                    className="font-medium text-blue-700 hover:underline"
                  >
                    {d.name}
                  </Link>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">Balance</div>
                    <div
                      className={`tabular-nums ${
                        Number(d.balance_commission || 0) > 0
                          ? "text-rose-600 font-semibold"
                          : "text-slate-700"
                      }`}
                    >
                      {fmtINR(d.balance_commission)}
                    </div>
                  </div>
                </div>

                <div className="mt-2 text-sm">
                  <div className="text-slate-500">Linked Customer</div>
                  {d.linked_customer ? (
                    <div className="mt-1">
                      <Link
                        to={`/customers/${d.linked_customer.customer_id}/passbook`}
                        className="text-blue-700 hover:underline"
                      >
                        {d.linked_customer.name}
                      </Link>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                        <span className="rounded-full bg-slate-100 px-2 py-0.5">
                          {d.linked_customer.type || "-"}
                        </span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5">
                          {d.linked_customer.commission_percent || 0}%
                        </span>
                        {typeBadge(d.linked_customer.commission_type)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-slate-400">—</div>
                  )}
                </div>

                <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
                  <div>
                    <div className="text-slate-500">Total</div>
                    <div className="tabular-nums">{fmtINR(d.total_commission)}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">Paid</div>
                    <div className="tabular-nums">{fmtINR(d.commission_paid)}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">Balance</div>
                    <div className="tabular-nums">{fmtINR(d.balance_commission)}</div>
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <Link
                    to={`/doctors/edit/${d.id}`}
                    className="flex-1 rounded-md bg-blue-600 px-3 py-2 text-center text-xs font-medium text-white shadow-sm hover:bg-blue-700"
                  >
                    Edit
                  </Link>
                  <button
                    onClick={() => handleAddPaymentClick(d)}
                    className="flex-1 rounded-md bg-emerald-600 px-3 py-2 text-center text-xs font-medium text-white shadow-sm hover:bg-emerald-700"
                  >
                    Add Payment
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Modal */}
     {isModalOpen && selectedDoctor && (
  <Modal onClose={() => setIsModalOpen(false)}>
    {/* width guard so content never overflows the modal card */}
    <div className="w-[min(92vw,680px)] max-w-full">
      {/* Header */}
      <div className="mb-2 flex items-start justify-between">
        <div>
          <h3 className="text-xl font-semibold text-slate-900">
            Add Commission Payment
          </h3>
          <div className="mt-1 text-sm text-slate-600">
            Doctor:{" "}
            <span className="font-medium text-slate-900">
              {selectedDoctor.name}
            </span>
          </div>
        </div>

        <div className="text-right">
          <div className="text-xs text-slate-500">Balance</div>
          <div className="text-base font-semibold tabular-nums">
            {new Intl.NumberFormat("en-IN", {
              style: "currency",
              currency: "INR",
              maximumFractionDigits: 2,
            }).format(Number(selectedDoctor.balance_commission || 0))}
          </div>
        </div>
      </div>

      {/* Quick amount chips */}
      <div className="mb-3 flex flex-wrap gap-2">
       {[500, 1000, 2000].map((amt) => (
  <button
    key={amt}
    type="button"
    onClick={() =>
      setForm((f) => ({
        ...f,
        amount: (Number(f.amount || 0) + Number(amt)).toFixed(2),
      }))
    }
    className="rounded-full border border-slate-300 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
  >
    ₹{amt}
  </button>
))}

        <button
          type="button"
          onClick={() =>
            setForm((f) => ({
              ...f,
              amount: Number(
                selectedDoctor.balance_commission || 0
              ).toFixed(2),
            }))
          }
          className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
          title="Use full balance"
        >
          Balance
        </button>
      </div>

      {/* Form */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Amount */}
        <div className="lg:col-span-1">
          <label className="mb-1 block text-xs text-slate-600">Amount</label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              ₹
            </span>
            <input
              type="number"
              step="0.01"
              placeholder="Amount"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="w-full rounded-md border border-slate-300 px-6 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Payment mode */}
        <div className="lg:col-span-1">
          <label className="mb-1 block text-xs text-slate-600">
            Payment Mode
          </label>
          <select
            value={form.payment_mode}
            onChange={(e) =>
              setForm({ ...form, payment_mode: e.target.value })
            }
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="Cash">Cash</option>
            <option value="Online">Online</option>
            <option value="Cheque">Cheque</option>
          </select>
        </div>

        {/* Date */}
        <div className="lg:col-span-1">
          <label className="mb-1 block text-xs text-slate-600">
            Payment Date
          </label>
          <input
            type="date"
            value={form.payment_date}
            onChange={(e) =>
              setForm({ ...form, payment_date: e.target.value })
            }
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Remark */}
        <div className="lg:col-span-1">
          <label className="mb-1 block text-xs text-slate-600">Remark</label>
          <input
            type="text"
            placeholder="Remark (optional)"
            value={form.remark}
            onChange={(e) => setForm({ ...form, remark: e.target.value })}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="mt-5 flex items-center justify-end gap-2">
        <button
          onClick={() => setIsModalOpen(false)}
          className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          onClick={handlePaymentSubmit}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
        >
          Save Payment
        </button>
      </div>
    </div>
  </Modal>
)}

    </div>
  );
}
